import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { isEmpty } from "lodash-es";
import { ReactNode } from "react";

import { ResultsPanelContextProvider } from "@/components/Search/SearchBar/components/ResultsPanel/context/resultsPanel.context";
import { useSearchBarContext } from "@/components/Search/SearchBar/context/searchBar.context";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

import { getLastAutocompleteItem } from "../../helpers/accessibility.helpers";

type ResultsPanelProps = {
    className?: string;
    children: ReactNode;
};

export const Base = ({ className, children }: ResultsPanelProps) => {
    const { isOpen, items, props, autocompleteState, autocompleteRef } =
        useSearchBarContext();

    if (!isOpen || !isDefined(items) || !isDefined(autocompleteState)) {
        return null;
    }

    const hasResults =
        !!Object.entries(items).length &&
        Object.values(items).some((resultList) => resultList.length > 0);

    const showResults = isEmpty(autocompleteState?.query) || hasResults;

    const handleResultsPanelKeyDown = createKeyHandler([
        {
            keys: [KeyNames.TAB],
            handler: (event) => {
                if (event.shiftKey) return;

                const lastAutocompleteItem = getLastAutocompleteItem();

                if (
                    isDefined(lastAutocompleteItem) &&
                    event.target === lastAutocompleteItem
                ) {
                    event.preventDefault();
                    autocompleteRef.current.input?.focus();
                }
            }
        },
        {
            keys: [KeyNames.ENTER],
            handler: () => {
                const focussedElement = document.activeElement;
                if (!(focussedElement instanceof HTMLAnchorElement)) return;

                focussedElement.click();
            }
        }
    ]);

    return (
        <ResultsPanelContextProvider
            showResults={showResults}
            onMouseDown={props.panel.onMouseDown}
            onKeyDown={handleResultsPanelKeyDown}
        >
            <div className={cn(className)}>{children}</div>
        </ResultsPanelContextProvider>
    );
};
